import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { AiChat } from "@/components/AiChat";
import { OnboardingTour } from "@/components/OnboardingTour";
import { CommandCenter } from "@/components/CommandCenter";
import { TaxDashboardCard } from "@/components/TaxDashboardCard";
import { AnomalyAlertsCard } from "@/components/AnomalyAlertsCard";
import { MonthlyReviewCard } from "@/components/MonthlyReviewCard";
import { MarketPulseCard } from "@/components/MarketPulseCard";
import { AgricultureMarginWatchlistCard } from "@/components/AgricultureMarginWatchlistCard";
import { VendorInsightsPanel } from "@/components/VendorInsightsPanel";
import { INDUSTRIES, isAgricultureBusiness } from "@/lib/industries";
import { ENTITY_TYPES, defaultEntityType } from "@/lib/entityTypes";
import { api, formatApiErrorDetail, describeApiError } from "@/lib/api";
import { PHASE_ORDER, ProgressBar } from "@/lib/phases";
import { labels } from "@/lib/mode";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Buildings, Trash, CaretRight, Users, ChartLineUp, Warning, Lock, Sparkle, Briefcase, Heartbeat, ShoppingCart, Hammer, House, Bed, ForkKnife, Scissors, Plant, Truck, CalendarBlank, Trophy, Target, Receipt, PiggyBank, TrendUp } from "@phosphor-icons/react";

const TMPL_ICONS = { Briefcase, Heartbeat, ShoppingCart, Hammer, House, Bed, ForkKnife, Scissors, Plant, Truck };

const HERO_EMPTY =
  "https://images.pexels.com/photos/7309483/pexels-photo-7309483.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const inputCls = "w-full px-4 py-3 rounded-md border bg-white text-sm outline-none focus:ring-2";
const inputStyle = { borderColor: "var(--bk-border)", color: "var(--bk-text)" };
const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TEMPLATES = INDUSTRIES;

function healthOf(pct) {
  if (pct >= 67) return { color: "var(--bk-success)", label: "Healthy" };
  if (pct >= 34) return { color: "#8A6A3B", label: "In progress" };
  return { color: "#B23B1F", label: "Needs attention" };
}

function StatCard({ icon: Icon, value, label, testid }) {
  return (
    <div className="bk-card p-6 flex items-center gap-4 bk-lift" data-testid={testid}>
      <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(74,111,165,0.12)" }}>
        <Icon size={24} weight="duotone" color="var(--bk-secondary)" />
      </span>
      <div>
        <p className="font-display text-2xl font-bold leading-none" style={{ color: "var(--bk-text)" }}>{value}</p>
        <p className="text-xs mt-1 uppercase tracking-[0.18em]" style={{ color: "var(--bk-text-2)" }}>{label}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const L = labels(user?.mode);
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState(null);
  const [runways, setRunways] = useState({});
  const [sub, setSub] = useState(null);
  const [marketPulse, setMarketPulse] = useState(null);
  const [agMargin, setAgMargin] = useState(null);
  const [arSummary, setArSummary] = useState(null);
  const [apSummary, setApSummary] = useState(null);
  const [receiptMatching, setReceiptMatching] = useState(null);
  const [savingsSummary, setSavingsSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", industry: "", entity_type: defaultEntityType(INDUSTRIES[0]?.entity_type), business_type: "", sub_type: "", state: "", county: "", market_pulse_opt_in: false });
  const [tmpl, setTmpl] = useState("general");
  const [subtypeCatalog, setSubtypeCatalog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showWinBack, setShowWinBack] = useState(false);

  const load = () => {
    api.get("/businesses").then((r) => setBusinesses(r.data)).catch(() => setBusinesses([]));
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
    api.get("/market-pulse").then((r) => setMarketPulse(r.data)).catch(() => setMarketPulse({ enabled: false }));
    api.get("/ar/summary").then((r) => setArSummary(r.data)).catch(() => {});
    api.get("/ap/summary").then((r) => setApSummary(r.data)).catch(() => {});
    api.get("/receipts/matching-summary").then((r) => setReceiptMatching(r.data)).catch(() => setReceiptMatching(null));
    api.get("/savings/summary").then((r) => setSavingsSummary(r.data)).catch(() => setSavingsSummary(null));
    api.get("/business-subtypes").then((r) => setSubtypeCatalog(r.data)).catch(() => setSubtypeCatalog(null));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!Array.isArray(businesses) || businesses.length === 0) return;
    businesses.forEach((b) => {
      api.get(`/businesses/${b.id}/forecast/runway`)
        .then((r) => setRunways((m) => ({ ...m, [b.id]: r.data })))
        .catch(() => {});
    });
    const agBusinesses = businesses.filter((b) => isAgricultureBusiness(b));
    if (agBusinesses.length > 0) {
      Promise.all(agBusinesses.map((b) => api.get(`/businesses/${b.id}/agriculture/margin-watchlist`).then((r) => ({ ...r.data, business_name: b.name })).catch(() => null)))
        .then((rows) => {
          const valid = rows.filter(Boolean);
          valid.sort((a, b) => {
            const score = (item) => (item.input_ratio_severity === "critical" ? 3 : item.input_ratio_severity === "warning" ? 2 : 1) * 100 + (item.flagged_count || 0);
            return score(b) - score(a);
          });
          setAgMargin(valid[0] || null);
        })
        .catch(() => setAgMargin(null));
    } else {
      setAgMargin(null);
    }
  }, [businesses]);

  useEffect(() => {
    if (sub?.state === "expired" && businesses) {
      const today = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem("bk_winback") !== today) setShowWinBack(true);
    }
  }, [sub, businesses]);

  useEffect(() => {
    if (sub?.state !== "trial") return;
    const left = sub.trial_days_left ?? 99;
    if (left > 5 || left < 0) return;
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem("bk_trial_nudge") === today) return;
    localStorage.setItem("bk_trial_nudge", today);
    toast(
      left <= 1
        ? `Your free trial ends ${left === 0 ? "today" : "tomorrow"} — lock in 40% off for life`
        : `Only ${left} days left in your free trial — lock in 40% off for life`,
      {
        description: "Founding members keep 40% off forever. The discount is applied automatically at checkout.",
        duration: 9000,
        action: { label: "See plans", onClick: () => navigate("/pricing") },
      }
    );
  }, [sub]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissWinBack = () => {
    localStorage.setItem("bk_winback", new Date().toISOString().slice(0, 10));
    setShowWinBack(false);
  };

  const atLimit = sub && sub.max_clients !== null && businesses && businesses.length >= sub.max_clients;

  const openCreate = () => {
    if (atLimit) {
      toast.info(`You've reached your ${sub.name} plan limit. Upgrade to add more ${L.entityPlural}.`);
      navigate("/pricing");
      return;
    }
    setOpen(true);
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/businesses", form);
      setOpen(false);
      setForm({ name: "", industry: "", entity_type: defaultEntityType(INDUSTRIES[0]?.entity_type), business_type: "", sub_type: "", state: "", county: "", market_pulse_opt_in: false });
      setTmpl("general");
      toast.success(`${L.Entity} created`);
      navigate(`/business/${data.id}?qb_offer=1`);
    } catch (err) {
      if (err.response?.status === 402) {
        setOpen(false);
        toast.info(formatApiErrorDetail(err.response?.data?.detail));
        navigate("/pricing");
      } else {
        toast.error(describeApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its progress?`)) return;
    await api.delete(`/businesses/${id}`);
    toast.success(`${L.Entity} deleted`);
    load();
  };

  const pctOf = (b) => {
    const d = PHASE_ORDER.reduce((a, p) => a + (b.progress[p]?.done || 0), 0);
    const t = PHASE_ORDER.reduce((a, p) => a + (b.progress[p]?.total || 0), 0);
    return { d, t, pct: t ? Math.round((d / t) * 100) : 0 };
  };

  const stats = businesses && businesses.length > 0 ? {
    count: businesses.length,
    avg: Math.round(businesses.reduce((a, b) => a + pctOf(b).pct, 0) / businesses.length),
    attention: businesses.filter((b) => pctOf(b).pct < 34).length,
  } : null;

  const openAgCategory = (businessId, category) => {
    navigate(`/business/${businessId}?tab=books&category=${encodeURIComponent(category)}`);
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="bk-page-shell py-10 sm:py-12">
        <div className="rounded-[28px] p-8 sm:p-10 mb-8 relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(26,39,68,0.98) 0%, rgba(74,111,165,0.98) 100%)" }} data-testid="dashboard-hero">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(232,168,56,0.18),transparent_26%)]" />
          <div className="relative flex flex-wrap items-end justify-between gap-4">
            <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "rgba(255,255,255,0.72)" }}>
              {L.workspaceTitle}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 text-white" style={{ color: "#fff" }}>
              Welcome, {user?.name || "there"}.
            </h1>
            <p className="text-base mt-2 max-w-2xl" style={{ color: "rgba(255,255,255,0.78)" }}>
              {sub?.state === "expired"
                ? "Your trial has ended — choose a plan to pick up where you left off."
                : sub && sub.max_clients !== null
                ? `${businesses?.length ?? 0} of ${sub.max_clients} ${L.entityPlural} used${sub.state === "trial" ? " during your free trial." : ` on your ${sub.name} plan.`}`
                : `Pick a ${L.entity} to continue, or add a new one.`}
            </p>
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium" style={{ background: "rgba(255,249,240,0.14)", color: "#fff", border: "1px solid rgba(232,168,56,0.22)" }}>
              <Sparkle size={16} weight="fill" style={{ color: "var(--bk-gold)" }} /> Rowan keeps the books calm, clear, and moving.
            </div>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-business-btn" onClick={openCreate} className="bk-btn-primary px-6 py-3 text-sm font-semibold flex items-center gap-2">
                {atLimit ? <Lock size={18} weight="duotone" /> : <Plus size={18} weight="bold" />} {L.newEntity}
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Add a {L.entity}</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4 mt-2" data-testid="create-business-form">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Start from a template</label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-1.5" data-testid="template-picker">
                    {TEMPLATES.map((t) => {
                      const Icon = TMPL_ICONS[t.icon] || Briefcase;
                      const active = tmpl === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          data-testid={`template-${t.key}`}
                          onClick={() => { setTmpl(t.key); setForm((f) => ({ ...f, business_type: t.business_type, industry: t.industry, entity_type: t.entity_type, sub_type: "" })); }}
                          className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg border text-center transition-all"
                          style={{ borderColor: active ? "var(--bk-accent)" : "var(--bk-border)", borderWidth: active ? 2 : 1, background: active ? "var(--bk-page)" : "#fff" }}
                        >
                          <Icon size={22} weight="duotone" color={active ? "var(--bk-accent)" : "var(--bk-text-2)"} />
                          <span className="text-xs font-semibold leading-tight" style={{ color: active ? "var(--bk-accent)" : "var(--bk-text)" }}>{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs mt-1.5" style={{ color: "var(--bk-text-2)" }} data-testid="template-blurb">
                    {(() => { const t = TEMPLATES.find((x) => x.key === tmpl); return t?.note ? `${t.blurb} · ${t.note}` : t?.blurb; })()}
                  </p>
                </div>
                {(() => {
                  const subs = subtypeCatalog?.[form.business_type || ""] || [];
                  if (!subs.length) return null;
                  const selected = subs.find((s) => s.key === form.sub_type);
                  const tLabel = TEMPLATES.find((x) => x.key === tmpl)?.label || "business";
                  return (
                    <div data-testid="subtype-picker">
                      <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>
                        What kind of {tLabel.toLowerCase()}? <span className="normal-case font-normal tracking-normal">(optional — tailors Rowan's setup & categories)</span>
                      </label>
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        {subs.map((s) => {
                          const active = form.sub_type === s.key;
                          return (
                            <button
                              key={s.key}
                              type="button"
                              data-testid={`subtype-${s.key}`}
                              onClick={() => setForm((f) => ({ ...f, sub_type: active ? "" : s.key }))}
                              className="px-3 py-1.5 rounded-full border text-xs font-semibold transition-all"
                              style={{ borderColor: active ? "var(--bk-accent)" : "var(--bk-border)", borderWidth: active ? 2 : 1, background: active ? "var(--bk-rowan-bg)" : "#fff", color: active ? "var(--bk-accent)" : "var(--bk-text)" }}
                            >
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                      {selected && (
                        <p className="text-xs mt-1.5 italic" style={{ color: "var(--bk-text-2)" }} data-testid="subtype-blurb">
                          Rowan: “{selected.rowan_note}”
                        </p>
                      )}
                    </div>
                  );
                })()}
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>{L.Entity} name *</label>
                  <input data-testid="business-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="Acme Coffee LLC" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Industry</label>
                  <input data-testid="business-industry-input" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="Food & beverage" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Entity type</label>
                  <select data-testid="business-entity-input" value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} className={inputCls + " mt-1.5"} style={inputStyle}>
                    {ENTITY_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>State</label>
                    <input data-testid="business-state-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="Montana" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>County / region</label>
                    <input data-testid="business-county-input" value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} className={inputCls + " mt-1.5"} style={inputStyle} placeholder="Gallatin County" />
                  </div>
                </div>
                <label className="rounded-2xl border p-4 flex items-start gap-3 cursor-pointer" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }} data-testid="market-pulse-opt-in-toggle">
                  <input type="checkbox" checked={!!form.market_pulse_opt_in} onChange={(e) => setForm({ ...form, market_pulse_opt_in: e.target.checked })} data-testid="market-pulse-opt-in-checkbox" className="mt-1" />
                  <span>
                    <span className="block text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Help improve IronRoot by anonymously sharing aggregated business trends with other owners in your area. Your data is never individually identifiable.</span>
                    <span className="block text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>We only store weekly buckets like revenue ranges, expense buckets, region, and top categories — never your exact numbers.</span>
                  </span>
                </label>
                <DialogFooter>
                  <button data-testid="save-business-btn" type="submit" disabled={saving} className="bk-btn-primary px-6 py-2.5 rounded-md text-sm font-semibold disabled:opacity-60">
                    {saving ? "Creating…" : `Create ${L.entity}`}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {sub?.state === "trial" && (() => {
          const urgent = (sub.trial_days_left ?? 99) <= 3;
          return (
            <div data-testid="trial-banner" className="mb-8 rounded-2xl border px-5 py-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: urgent ? "#E7B6A8" : "rgba(232,168,56,0.32)", background: urgent ? "#FDECE8" : "var(--bk-rowan-bg)" }}>
              <div className="flex items-center gap-2.5">
                {urgent ? <Warning size={20} weight="duotone" color="#B23B1F" /> : <Sparkle size={20} weight="duotone" color="var(--bk-accent)" />}
                <p className="text-sm font-medium" style={{ color: "var(--bk-text)" }}>
                  {urgent
                    ? <>Your trial ends in <span className="font-bold">{sub.trial_days_left} day{sub.trial_days_left === 1 ? "" : "s"}</span> — choose a plan now to avoid losing access to your {L.entityPlural} and AI assistant.</>
                    : <>You're on a free trial — <span className="font-bold">{sub.trial_days_left} day{sub.trial_days_left === 1 ? "" : "s"} left</span>. Pick a plan anytime to keep your workspace after it ends.</>}
                </p>
              </div>
              <button onClick={() => navigate("/pricing")} data-testid="trial-see-plans" className="bk-btn-primary px-4 py-2 text-sm font-semibold shrink-0">{urgent ? "Choose a plan" : "See plans"}</button>
            </div>
          );
        })()}
        {sub?.state === "expired" && (
          <div data-testid="expired-banner" className="mb-8 rounded-2xl border px-5 py-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "#E7B6A8", background: "#FDECE8" }}>
            <div className="flex items-center gap-2.5">
              <Lock size={20} weight="duotone" color="#B23B1F" />
              <p className="text-sm font-medium" style={{ color: "var(--bk-text)" }}>
                Your free trial has ended. Your data is safe — choose a plan to add {L.entityPlural} and use the AI assistant again.
              </p>
            </div>
            <button onClick={() => navigate("/pricing")} data-testid="expired-choose-plan" className="bk-btn-primary px-4 py-2 text-sm font-semibold shrink-0">Choose a plan</button>
          </div>
        )}

        {stats && (
          <>
            <CommandCenter refreshKey={businesses?.length} />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-10">
              <StatCard icon={Users} value={stats.count} label={`Active ${L.entityPlural}`} testid="stat-total" />
              <StatCard icon={ChartLineUp} value={`${stats.avg}%`} label="Average completion" testid="stat-avg" />
              <StatCard icon={Warning} value={stats.attention} label="Need attention" testid="stat-attention" />
              <StatCard icon={Sparkle} value={money(arSummary?.outstanding_total || 0)} label="Open A/R" testid="dashboard-ar-summary-card" />
              <StatCard icon={Briefcase} value={money(apSummary?.outstanding_total || 0)} label="Open A/P" testid="dashboard-ap-summary-card" />
            </div>
            <MonthlyReviewCard />
            <TaxDashboardCard />
            <AnomalyAlertsCard />
            {savingsSummary && (savingsSummary.total_saved > 0 || savingsSummary.hikes_count > 0) && (
              <div className="bk-card p-6 mb-10" data-testid="dashboard-savings-card">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <PiggyBank size={24} weight="duotone" color="var(--bk-success)" className="mt-0.5 shrink-0" />
                    <div>
                      <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Rowan&apos;s saved you</h3>
                      <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="dashboard-savings-message">{savingsSummary.rowan_message}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 min-w-0 max-w-full">
                    <div className="rounded-2xl border px-5 py-3 text-center" style={{ borderColor: "rgba(91,138,111,0.3)", background: "var(--bk-success-bg)" }} data-testid="dashboard-savings-total">
                      <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--bk-success)" }}>Saved so far</p>
                      <p className="font-display text-2xl font-bold" style={{ color: "var(--bk-success)" }}>{money(savingsSummary.total_saved)}</p>
                    </div>
                    {savingsSummary.hikes_extra_monthly > 0 && (
                      <div className="rounded-2xl border px-5 py-3 text-center" style={{ borderColor: "rgba(232,168,56,0.35)", background: "#FFF4D9" }} data-testid="dashboard-savings-hikes">
                        <p className="text-[10px] uppercase tracking-[0.14em] font-semibold inline-flex items-center gap-1" style={{ color: "#8A6A3B" }}><TrendUp size={10} weight="bold" /> Hikes caught</p>
                        <p className="font-display text-2xl font-bold" style={{ color: "#8A6A3B" }}>{money(savingsSummary.hikes_extra_monthly)}/mo</p>
                      </div>
                    )}
                    {savingsSummary.top_business_id && (
                      <button onClick={() => navigate(`/business/${savingsSummary.top_business_id}?tab=books`)} data-testid="dashboard-savings-open" className="bk-btn-primary px-4 py-2 text-sm font-semibold">See details</button>
                    )}
                  </div>
                </div>
              </div>
            )}
            {receiptMatching && (receiptMatching.auto_matched_count > 0 || receiptMatching.needs_attention_count > 0) && (
              <div className="bk-card p-6 mb-10" data-testid="dashboard-receipt-matching-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Receipt size={20} weight="duotone" color="var(--bk-accent)" />
                      <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Receipt Matching</h3>
                    </div>
                    <p className="text-sm mt-3" style={{ color: "var(--bk-text)" }} data-testid="dashboard-receipt-matching-message">{receiptMatching.rowan_message}</p>
                  </div>
                  {receiptMatching.business_id && <button onClick={() => navigate(`/business/${receiptMatching.business_id}?tab=receipts`)} data-testid="dashboard-receipt-matching-open" className="bk-btn-primary px-4 py-2 text-sm font-semibold">Open receipts</button>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                  <div className="rounded-2xl border p-4" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="dashboard-receipt-matching-auto-count">
                    <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Matched automatically this month</p>
                    <p className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-success)" }}>{receiptMatching.auto_matched_count}</p>
                  </div>
                  <div className="rounded-2xl border p-4" style={{ borderColor: "var(--bk-border)", background: "#fff" }} data-testid="dashboard-receipt-matching-attention-count">
                    <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Still need attention</p>
                    <p className="font-display text-2xl font-bold mt-2" style={{ color: "#B23B1F" }}>{receiptMatching.needs_attention_count}</p>
                  </div>
                </div>
              </div>
            )}
            {agMargin && (
              <AgricultureMarginWatchlistCard
                data={agMargin}
                onOpenCategory={(category) => openAgCategory(agMargin.business_id, category)}
              />
            )}
            <MarketPulseCard data={marketPulse} />
            <div className="mb-10">
              <VendorInsightsPanel
                title="Vendor Insights"
                subtitle="Rowan spots expensive loyalties, creeping prices, and negotiation moments across all your businesses."
                testIdPrefix="dashboard-vendor-insights"
                showBusinessName
                maxItems={3}
              />
            </div>
          </>
        )}

        {businesses === null ? (
          <p style={{ color: "var(--bk-text-2)" }}>Loading…</p>
        ) : businesses.length === 0 ? (
          <div className="bk-empty-state overflow-hidden grid md:grid-cols-2 items-center shadow-[0_12px_36px_rgba(26,39,68,0.08)]" data-testid="empty-state">
            <div className="p-10 lg:p-12">
              <h2 className="font-display text-2xl font-bold mb-3" style={{ color: "var(--bk-text)" }}>Nothing here yet. Let&apos;s change that.</h2>
              <p className="text-base mb-6" style={{ color: "var(--bk-text-2)" }}>
                Add your first {L.entity} to unlock the guided setup roadmap, your Books ledger, and the calm before the profit.
              </p>
              <button onClick={openCreate} className="bk-btn-primary px-6 py-3 text-sm font-semibold inline-flex items-center gap-2">
                <Plus size={18} weight="bold" /> {L.addFirst}
              </button>
            </div>
            <img src={HERO_EMPTY} alt="Entrepreneur planning" className="h-64 md:h-full w-full object-cover" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {businesses.map((b, i) => {
              const { d, t, pct } = pctOf(b);
              const h = healthOf(pct);
              return (
                <div key={b.id} data-testid={`business-card-${i}`} className="bk-card bk-lift p-6 flex flex-col bk-fade-up" style={{ animationDelay: `${0.05 * i}s` }}>
                  <div className="flex items-start justify-between">
                    <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "rgba(74,111,165,0.12)" }}>
                      <Buildings size={24} weight="duotone" color="var(--bk-secondary)" />
                    </span>
                    {b.access_role === "owner" ? (
                      <button onClick={() => remove(b.id, b.name)} data-testid={`delete-business-${i}`} className="p-2 rounded-full hover:bg-[#F5F5F0] transition-colors" title="Delete">
                        <Trash size={18} weight="duotone" color="var(--bk-text-2)" />
                      </button>
                    ) : (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }} data-testid={`business-role-pill-${i}`}>{b.access_role_label}</span>
                    )}
                  </div>
                  <h3 className="font-display text-xl font-bold mt-4" style={{ color: "var(--bk-text)" }}>{b.name}</h3>
                  <div className="flex items-center gap-1.5 mb-4 mt-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: h.color }} data-testid={`health-${i}`} />
                    <span className="text-xs font-medium" style={{ color: h.color }}>{h.label}</span>
                    <span className="text-xs" style={{ color: "var(--bk-disabled)" }}>· {[b.entity_type, b.industry].filter(Boolean).join(" · ") || "No details"}{b.access_role !== "owner" && b.owner_name ? ` · Owner: ${b.owner_name}` : ""}</span>
                  </div>
                  {runways[b.id]?.has_history && (runways[b.id].daily_net !== 0 || runways[b.id].starting_balance > 0) && (() => {
                    const rw = runways[b.id];
                    const positive = rw.runway_days === null || rw.runway_days === undefined;
                    let c = "var(--bk-success)", bg = "var(--bk-success-bg)";
                    if (!positive) {
                      if (rw.runway_days <= 30) { c = "var(--bk-energy)"; bg = "var(--bk-energy-bg)"; }
                      else if (rw.runway_days <= 60) { c = "#B45309"; bg = "#FEF3C7"; }
                    }
                    return (
                      <div className="mb-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold w-fit"
                        style={{ background: bg, color: c }} data-testid={`runway-${i}`}>
                        <ChartLineUp size={13} weight="bold" />
                        {positive ? "Cash-flow positive" : (rw.runway_days === 0 ? "Out of cash" : `~${rw.runway_days} days of cash`)}
                      </div>
                    );
                  })()}
                  {runways[b.id]?.next_bill && (
                    <div className="-mt-2 mb-4 flex items-center gap-1.5 text-xs" style={{ color: "var(--bk-text-2)" }} data-testid={`next-bill-${i}`}>
                      <CalendarBlank size={13} weight="bold" />
                      Next bill: <span className="font-semibold" style={{ color: "var(--bk-text)" }}>
                        ${Number(runways[b.id].next_bill.amount).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                      </span> · {runways[b.id].next_bill.name} on {new Date(runways[b.id].next_bill.due_date + "T00:00:00").toLocaleString("en-US", { month: "short", day: "numeric" })}
                    </div>
                  )}
                  {runways[b.id]?.runway_goal_days && (
                    <div className="-mt-2 mb-4 flex items-center gap-1.5 text-xs" data-testid={`goal-${i}`}>
                      {runways[b.id].goal_met ? (
                        <span className="inline-flex items-center gap-1 font-semibold" style={{ color: "var(--bk-success)" }}>
                          <Trophy size={13} weight="fill" /> Goal reached: {runways[b.id].runway_goal_days}-day runway
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1" style={{ color: "var(--bk-text-2)" }}>
                          <Target size={13} weight="bold" /> Goal: {runways[b.id].runway_goal_days} days
                          {typeof runways[b.id].runway_days === "number" && (
                            <span style={{ color: "var(--bk-disabled)" }}> · {Math.max(0, runways[b.id].runway_goal_days - runways[b.id].runway_days)} to go</span>
                          )}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="mt-auto space-y-3">
                    <ProgressBar done={d} total={t} />
                    <button onClick={() => navigate(`/business/${b.id}`)} data-testid={`open-business-${i}`} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold border transition-colors hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-accent)", color: "var(--bk-accent)" }}>
                      Open <CaretRight size={16} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={showWinBack} onOpenChange={(o) => { if (!o) dismissWinBack(); }}>
        <DialogContent className="bg-white" aria-describedby={undefined} data-testid="winback-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Welcome back — your work is safe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>
              Your free trial has ended, but nothing's lost. Look how far you got:
            </p>
            <div className="flex gap-3">
              <div className="flex-1 rounded-md p-4 text-center" style={{ background: "var(--bk-page)" }}>
                <p className="font-display text-3xl font-bold" style={{ color: "var(--bk-text)" }} data-testid="winback-biz-count">{businesses?.length ?? 0}</p>
                <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{businesses?.length === 1 ? L.entity : L.entityPlural} started</p>
              </div>
              <div className="flex-1 rounded-md p-4 text-center" style={{ background: "var(--bk-page)" }}>
                <p className="font-display text-3xl font-bold" style={{ color: "var(--bk-text)" }} data-testid="winback-steps">{businesses ? businesses.reduce((a, b) => a + pctOf(b).d, 0) : 0}</p>
                <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>steps completed</p>
              </div>
            </div>
            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>
              Pick a plan and pick up exactly where you left off — no re-doing anything.
            </p>
            <div className="flex gap-3">
              <button onClick={() => { dismissWinBack(); navigate("/pricing"); }} data-testid="winback-choose-plan" className="bk-btn-primary flex-1 py-2.5 text-sm font-semibold">Choose a plan</button>
              <button onClick={dismissWinBack} data-testid="winback-later" className="px-5 py-2.5 rounded-md text-sm font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}>Maybe later</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AiChat />
      <OnboardingTour shouldShow={sub?.state === "trial"} onAddBusiness={openCreate} />
    </div>
  );
}
